import { useRef } from 'react';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Modal from '@sdk/components/ui/modal/Modal';
import Text14 from '@sdk/components/ui/typography/Text14';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import moneyDisplay from '@sdk/common/currency/moneyDisplay';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import { SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import DashboardBlueSvg from '@web/assets/svg/Icons/32/dashboard-blue.svg';
import CurrentBillingPricing from '@web/account/billing/CurrentBillingPricing/CurrentBillingPricing';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import type { SubscriptionUtilsType } from '@web/account/types';

import { useDowngradeOfferModal } from './downgradeOfferModal.hooks';

type Props = {
  isVisible: boolean;
  accept: () => void;
  decline: () => void;
  subscriptionUtils: SubscriptionUtilsType;
  orgSettings: OrgSettings;
  currency: CurrencyType;
  reasonId: number;
  reasonText: string;
};

const Grant50PercentOffModal = ({
  isVisible,
  accept,
  decline,
  subscriptionUtils,
  orgSettings,
  currency,
  reasonId,
  reasonText,
}: Props) => {
  const config = useRef({ noDismiss: true }).current;
  const { acceptDowngradeOffer } = useDowngradeOfferModal(accept, reasonId, reasonText);
  const { billingInfo } = useBillingInfo();

  return (
    <>
      <Modal fixed config={config} isVisible={isVisible}>
        <ModalBodyWithHeader
          width={620}
          titleThemeColor={ThemeColorType.primary}
          titleText={gettext('Downgrade to Premium')}
          titleIcon={<DashboardBlueSvg />}
          close={decline}>
          <Column mBottom={10}>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext(
                'Instead of canceling your Business subscription, you can opt for Premium, which gives you access to labor cost and time tracking tools at a lower cost.'
              )}
            </Text14>
          </Column>
          <Column mBottom={20}>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext('After the change your account will be billed as follows:')}
            </Text14>
          </Column>
          <HorizontalSeparator />
          <CurrentBillingPricing
            level={SUBSCRIPTION_LEVELS.PREMIUM}
            isYearlyPlan={billingInfo.isYearlyPlan}
            subscriptionUtils={subscriptionUtils}
            orgSettings={orgSettings}
            currency={currency}
            ignoreNextCharge={true}
          />
          {billingInfo.isYearlyPlan && (
            <>
              <Row mTop={20}>
                <HorizontalSeparator />
              </Row>
              <Row mTop={20}>
                <Text14 semiBold themeColor={ThemeColorType.gray}>
                  {gettext('Paying today')}
                </Text14>
                <Row mLeft={'auto'}>
                  <Text14 semiBold themeColor={ThemeColorType.gray}>
                    {moneyDisplay({ value: 0, currency })}
                  </Text14>
                </Row>
              </Row>
            </>
          )}
          <ModalFooterActionButtons
            cancel={decline}
            submit={acceptDowngradeOffer}
            cancelText={gettext('Nevermind')}
            submitText={gettext('Downgrade to Premium')}
          />
        </ModalBodyWithHeader>
      </Modal>
    </>
  );
};

export default Grant50PercentOffModal;
